import { Icon, Portal } from '@chakra-ui/react';
import { InvoiceTransformationTargetEnum } from '@monetize/types/app';
import { FunctionComponent as FC, useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';
import {
  useGetInvoiceTransformationAvailableFields,
  useGetInvoiceTransformationById,
} from '../../api/invoiceTransformationService';
import { useConfirmModal } from '../../services/confirmModal';
import {
  MBox,
  MButton,
  MCircularProgress,
  MDivider,
  MDrawer,
  MDrawerBody,
  MDrawerCloseButton,
  MDrawerContent,
  MDrawerFooter,
  MDrawerHeader,
  MDrawerOverlay,
  MDrawerPositioner,
  MFlex,
} from '../Monetize';
import {
  INVOICE_TRANSFORMATION_FORM_ID,
  InvoiceTransformationForm,
} from './InvoiceTransformationForm';

interface InvoiceTransformationDrawerProps {
  billGroupId: string;
  accountId: string;
  onClose: () => void;
}

export const InvoiceTransformationDrawer: FC<
  InvoiceTransformationDrawerProps
> = ({ billGroupId, accountId, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  // Opens read-only when existing rules are present; the user clicks Edit to
  // modify. New configurations (no rules) open directly in edit mode.
  const [isEditing, setIsEditing] = useState(false);
  // Bumped when an edit is cancelled — remounting the form discards all
  // in-progress state (form values plus local UI state like expanded rules).
  const [formKey, setFormKey] = useState(0);
  const { showConfirmModal, closeModal: closeConfirmationModal } =
    useConfirmModal();

  const {
    data: invoiceTransformation,
    isLoading: isInvoiceTransformationLoading,
  } = useGetInvoiceTransformationById(billGroupId);

  const { data: availableFields, isLoading: isAvailableFieldsLoading } =
    useGetInvoiceTransformationAvailableFields(
      InvoiceTransformationTargetEnum.INVOICE_ITEM,
    );

  const isLoading = isInvoiceTransformationLoading || isAvailableFieldsLoading;

  const hasExistingRules = (
    invoiceTransformation?.invoiceTransformations ?? []
  ).some((transformation) => transformation.invoiceLineRules.length > 0);

  // Once the configuration has loaded, start in edit mode only when there's
  // nothing saved yet.
  useEffect(() => {
    if (!isInvoiceTransformationLoading && !hasExistingRules) {
      setIsEditing(true);
    }
  }, [isInvoiceTransformationLoading, hasExistingRules]);

  const handleSave = () => {
    onClose();
  };

  // Cancel returns to the read-only view instead of closing the drawer.
  const handleCancelEdit = () => {
    const revertToReadOnly = () => {
      setIsEditing(false);
      setFormKey((prev) => prev + 1);
    };
    if (isDirty) {
      showConfirmModal({
        title: 'Unsaved Changes',
        description:
          'You have unsaved changes. Are you sure you want to discard them?',
        onYes: () => {
          closeConfirmationModal();
          revertToReadOnly();
        },
        yesBtnProps: { variant: 'primary' },
        noBtnProps: { variant: 'cancel' },
      });
    } else {
      revertToReadOnly();
    }
  };

  const handleClose = () => {
    if (isDirty) {
      showConfirmModal({
        title: 'Unsaved Changes',
        description:
          'You have unsaved changes. Are you sure you want to close?',
        onYes: () => {
          closeConfirmationModal();
          onClose();
        },
        yesBtnProps: { variant: 'primary' },
        noBtnProps: { variant: 'cancel' },
      });
    } else {
      onClose();
    }
  };

  return (
    <MBox position="relative">
      <MDrawer
        open
        placement="end"
        onOpenChange={(e) => {
          if (!e.open) {
            handleClose();
          }
        }}
        size="lg"
        preventScroll={false}
      >
        <Portal>
          <MDrawerOverlay />
          <MDrawerPositioner>
            <MDrawerContent>
              <MDrawerCloseButton
                color="tPurple.base"
                mt={-0.5}
                onClick={handleClose}
              >
                <Icon asChild>
                  <MdClose />
                </Icon>
              </MDrawerCloseButton>
              <MDrawerHeader>Output Configuration</MDrawerHeader>
              <MDrawerBody px={4} py={4} bgColor="tGray.sidebarDark">
                {isLoading && (
                  <MFlex w="full" h="full" justify="center" align="center">
                    <MCircularProgress isIndeterminate />
                  </MFlex>
                )}
                {!isLoading && (
                  <InvoiceTransformationForm
                    key={formKey}
                    billGroupId={billGroupId}
                    accountId={accountId}
                    initialData={invoiceTransformation}
                    availableFields={availableFields ?? []}
                    isEditing={isEditing}
                    onSave={handleSave}
                    setIsSubmitting={setIsSubmitting}
                    onDirtyChange={setIsDirty}
                  />
                )}
              </MDrawerBody>

              <MDivider />

              <MDrawerFooter
                display="flex"
                justifyContent="center"
                alignItems="center"
                gap={2}
              >
                {isEditing ? (
                  <>
                    <MButton
                      type="button"
                      variant="cancel"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </MButton>
                    <MButton
                      type="button"
                      variant="primary"
                      // Submit programmatically instead of via `type="submit"`
                      // form association: a submit button associated with the form
                      // would otherwise capture the click that mounts it when
                      // switching from read-only to edit mode, triggering an
                      // accidental save.
                      onClick={() => {
                        const form = document.getElementById(
                          INVOICE_TRANSFORMATION_FORM_ID,
                        );
                        if (form instanceof HTMLFormElement) {
                          form.requestSubmit();
                        }
                      }}
                      isLoading={isSubmitting}
                      isDisabled={isLoading || isSubmitting}
                    >
                      Save
                    </MButton>
                  </>
                ) : (
                  <>
                    <MButton
                      type="button"
                      variant="cancel"
                      onClick={handleClose}
                    >
                      Close
                    </MButton>
                    <MButton
                      type="button"
                      variant="primary"
                      onClick={() => setIsEditing(true)}
                      isDisabled={isLoading}
                    >
                      Edit
                    </MButton>
                  </>
                )}
              </MDrawerFooter>
            </MDrawerContent>
          </MDrawerPositioner>
        </Portal>
      </MDrawer>
    </MBox>
  );
};
